import React, { useState, useEffect } from 'react';
import { Download, ClipboardList, AlertCircle } from 'lucide-react';

const DSR = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [fuelType, setFuelType] = useState('Petrol');
  const [openingBalance, setOpeningBalance] = useState('');

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const queryParams = new URLSearchParams({ month, year, fuelType });
      if (openingBalance) queryParams.append('openingBalance', openingBalance);
      const res = await fetch(`/api/reports/dsr-data?${queryParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const json = await res.json();
      if (json.error) {
        setError(json.error);
        setData(null);
      } else {
        setData(json);
      }
    } catch (err) {
      setError(err.message);
      setData(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, fuelType, openingBalance]);

  const handleExport = async () => {
    const queryParams = new URLSearchParams({ month, year, fuelType });
    if (openingBalance) queryParams.append('openingBalance', openingBalance);
    try {
      const res = await fetch(`/api/export/dsr?${queryParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Failed to export DSR');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DSR_${fuelType}_${month}_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const fmt = (v) => (v === null || v === undefined) ? '-' : Number(v).toFixed(2);
  const dayOf = (dateStr) => new Date(dateStr).getDate();

  const renderTable = () => {
    if (!data) return null;
    const { tanks, nozzles, rows } = data;

    const totalReceipt = rows.reduce((sum, r) => sum + (r.receipt || 0), 0);
    const totalTesting = rows.reduce((sum, r) => sum + (r.testing || 0), 0);
    const tankColSpan = tanks.length * 2;
    const meterColSpan = nozzles.length || 1;

    return (
      <div className="overflow-x-auto border border-slate-200 rounded-xl shadow-sm bg-white mt-6">
        <table className="w-full text-sm text-left border-collapse min-w-[1100px]">
          <thead className="bg-slate-50 text-slate-700 font-bold uppercase text-xs">
            <tr className="border-b border-slate-200">
              <th className="p-3 border-r border-slate-200 text-center align-middle" rowSpan={2}>Date</th>
              {tanks.map(t => (
                <th key={`dip-h-${t.id}`} className="p-2 border-r border-slate-200 text-center" colSpan={2}>{t.tank_name}</th>
              ))}
              <th className="p-3 border-r border-slate-200 text-center align-middle bg-blue-50/50" rowSpan={2}>Opening Stock</th>
              <th className="p-3 border-r border-slate-200 text-center align-middle" rowSpan={2}>Receipt</th>
              <th className="p-3 border-r border-slate-200 text-center align-middle bg-blue-100/50" rowSpan={2}>Total Stock</th>
              <th className="p-2 border-r border-slate-200 text-center" colSpan={nozzles.length || 1}>Opening Meter Reading (Pumps)</th>
              <th className="p-3 border-r border-slate-200 text-center align-middle" rowSpan={2}>Testing</th>
              <th className="p-3 border-r border-slate-200 text-center align-middle bg-emerald-50" rowSpan={2}>Sales</th>
              <th className="p-3 text-center align-middle bg-emerald-100" rowSpan={2}>Cumulative Sales</th>
            </tr>
            <tr className="border-b border-slate-200 bg-slate-100">
              {tanks.map(t => (
                <React.Fragment key={`dip-sub-${t.id}`}>
                  <th className="p-2 border-r border-slate-200 text-center whitespace-nowrap">Dip</th>
                  <th className="p-2 border-r border-slate-200 text-center whitespace-nowrap">Water Dip</th>
                </React.Fragment>
              ))}
              {nozzles.length > 0 ? nozzles.map(n => (
                <th key={`noz-${n.id}`} className="p-2 border-r border-slate-200 text-center whitespace-nowrap">{n.pump_name} · N{n.nozzle_number}</th>
              )) : (
                <th className="p-2 border-r border-slate-200 text-center text-slate-400">-</th>
              )}
            </tr>
          </thead>
          <tbody className="text-slate-600 font-medium">
            {rows.map((row) => (
              <tr key={row.date} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="p-2 border-r border-slate-200 text-center font-semibold text-slate-800">{dayOf(row.date)}</td>
                {tanks.map(t => (
                  <React.Fragment key={`dip-${t.id}-${row.date}`}>
                    <td className="p-2 border-r border-slate-200 text-right">{fmt(row.dips[t.id])}</td>
                    <td className="p-2 border-r border-slate-200 text-right">{fmt(row.waterDips[t.id])}</td>
                  </React.Fragment>
                ))}
                <td className="p-2 border-r border-slate-200 text-right bg-blue-50/30">{fmt(row.openingStock)}</td>
                <td className="p-2 border-r border-slate-200 text-right">{fmt(row.receipt)}</td>
                <td className="p-2 border-r border-slate-200 text-right bg-blue-100/30 font-bold">{fmt(row.totalStock)}</td>
                {nozzles.length > 0 ? nozzles.map(n => (
                  <td key={`meter-${n.id}-${row.date}`} className="p-2 border-r border-slate-200 text-right">{fmt(row.meters[n.id])}</td>
                )) : (
                  <td className="p-2 border-r border-slate-200 text-center text-slate-400">-</td>
                )}
                <td className="p-2 border-r border-slate-200 text-right">{fmt(row.testing)}</td>
                <td className="p-2 border-r border-slate-200 text-right bg-emerald-50/40 font-semibold">{fmt(row.sales)}</td>
                <td className="p-2 text-right bg-emerald-100/40 font-bold text-emerald-900">{fmt(row.cumulativeSales)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-amber-50 font-bold text-slate-900 border-t-2 border-slate-300">
              <td colSpan={1 + tankColSpan} className="p-3 border-r border-slate-200 text-right">TOTAL</td>
              <td className="p-3 border-r border-slate-200 text-right bg-amber-100/50"></td>
              <td className="p-3 border-r border-slate-200 text-right bg-amber-100/50">{fmt(totalReceipt)}</td>
              <td className="p-3 border-r border-slate-200 bg-amber-50"></td>
              <td colSpan={meterColSpan} className="border-r border-slate-200 bg-amber-50"></td>
              <td className="p-3 border-r border-slate-200 text-right bg-amber-100/50">{fmt(totalTesting)}</td>
              <td className="p-3 border-r border-slate-200 bg-amber-50"></td>
              <td className="p-3 bg-amber-50"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-indigo-600" /> Daily Sales Record (DSR)
          </h1>
          <p className="text-slate-500 text-sm mt-1">Product-wise dip, stock, pump meter and sales register.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select className="input-field py-2 bg-white" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({length: 12}, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
          <input type="number" className="input-field py-2 w-24 bg-white" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          <select className="input-field py-2" value={fuelType} onChange={(e) => setFuelType(e.target.value)}>
            <option value="Petrol">Petrol</option>
            <option value="Diesel">Diesel</option>
            <option value="Xtragreen">Xtragreen</option>
          </select>
          <input
            type="number"
            className="input-field py-2 w-36"
            placeholder="Opening Balance"
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
            title="Leave empty if previous month's closing dip exists"
          />
          <button onClick={handleExport} disabled={!data || isLoading} className="btn-primary py-2 px-4 flex items-center gap-2">
            <Download className="w-4 h-4"/> Download Excel
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <p className="font-medium">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="p-12 text-center text-slate-500 font-medium animate-pulse">Building the daily sales record...</div>
      ) : (
        renderTable()
      )}
    </div>
  );
};

export default DSR;
